import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { API, useAuth } from "@/lib/auth";
import { ErrorBox, Freshness, Skel } from "@/components/market/Shared";
import { MarketRow } from "@/components/predict/MarketRow";
import { PicksStrip } from "@/components/predict/PicksStrip";
import { MarketSheet } from "@/components/predict/MarketSheet";
import { PaperPortfolio } from "@/components/predict/PaperPortfolio";
import { PicksBotCard } from "@/components/predict/PicksBotCard";
import { CATS } from "@/components/predict/utils";
import { SportsBoard } from "@/components/predict/SportsBoard";
import { Zones } from "@/components/predict/Zones";
import { ModeCard } from "@/components/engage/ModeCard";
import { Leaderboard } from "@/components/engage/Leaderboard";
import { PickAlerts } from "@/components/engage/PickAlerts";

const SORTS = [["popular", "Popular"], ["ending", "Ending soon"], ["close", "Closest calls"], ["movers", "Movers (1h)"]];

const CatInfo = ({ info }) => info ? (
  <section className="glass p-4 text-sm" data-testid="category-info">
    <div className="text-slate-200">{info.blurb}</div>
    <div className="text-xs text-slate-400 mt-1">{info.how}</div>
  </section>
) : null;

export default function Predict() {
  const { profile } = useAuth();
  const [cats, setCats] = useState(null);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("popular");
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { axios.get(`${API}/predict/categories`).then((r) => setCats(r.data)).catch(() => {}); }, []);
  const reqId = useRef(0);
  const load = () => {
    const id = ++reqId.current;
    return axios.get(`${API}/predict/markets`, { params: { category: cat, q, sort, limit: 40 } })
      .then((r) => { if (id === reqId.current) { setD(r.data); setErr(null); } })
      .catch(() => { if (id === reqId.current) setErr("Prediction markets are unavailable right now."); });
  };
  useEffect(() => { setD(null); const t = setTimeout(load, q ? 350 : 0); return () => clearTimeout(t); }, [cat, q, sort]); // eslint-disable-line react-hooks/exhaustive-deps
  const count = (id) => id === "all" ? cats?.total : cats?.categories.find((c) => c.id === id)?.count;
  return (
    <div className="space-y-5" data-testid="predict-page">
      <div><h1 className="font-head font-bold text-3xl tracking-tight">Prediction markets</h1>
        <p className="text-sm text-slate-400 mt-1">Every live Polymarket US market — sports, politics, crypto, culture and more — explained in plain English.</p></div>
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="category-chips">
        {CATS.map(([id, n]) => (
          <button key={id} onClick={() => setCat(id)} data-testid={`cat-${id}`}
            className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${cat === id ? "bg-[var(--cyan)] text-[#03141A] border-[var(--cyan)]" : "border-[var(--line)] text-slate-300 hover:border-[var(--cyan)]"}`}>
            {n}{count(id) != null && <span className="ml-1.5 text-[10px] opacity-70 font-num">{count(id)}</span>}
          </button>))}
      </div>
      {cat !== "all" && <CatInfo info={cats?.categories.find((c) => c.id === cat)} />}
      {profile && <ModeCard compact />}
      <PicksStrip category={cat} onOpen={setOpen} />
      <Zones onOpen={setOpen} />
      <PickAlerts />
      {profile && <PaperPortfolio onOpen={setOpen} />}
      <section className="space-y-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search teams, players, candidates, topics…" className="pl-9 h-11 bg-[var(--card)] border-[var(--line)] rounded-xl" data-testid="pm-search-input" />
          </div>
          <div className="flex gap-1 overflow-x-auto">{SORTS.map(([id, n]) => (
            <button key={id} onClick={() => setSort(id)} data-testid={`sort-${id}`}
              className={`shrink-0 px-3 h-11 rounded-xl text-xs font-semibold border ${sort === id ? "border-[var(--violet)] text-[var(--violet)]" : "border-[var(--line)] text-slate-400"}`}>{n}</button>))}</div>
        </div>
        {cat === "sports" ? <SportsBoard q={q} onOpen={setOpen} /> : <>
        {err && <ErrorBox msg={err} onRetry={load} />}
        {d && <Freshness d={d} testid="pm-freshness" />}
        <div className="glass divide-y divide-[var(--line)]" data-testid="pm-list">
          {!d && !err ? [1, 2, 3, 4].map((i) => <div key={i} className="p-3"><Skel h="h-12" /></div>) :
            d?.markets.length === 0 ? <p className="p-6 text-sm text-slate-400" data-testid="pm-empty">{sort === "movers" ? "No 1-hour price history yet — we sample every 5 minutes, check back soon." : "No markets match."}</p> :
              d?.markets.map((m) => <MarketRow key={m.slug} m={m} onOpen={setOpen} />)}
        </div>
        {d && d.count > d.markets.length && <p className="text-xs text-slate-500">Showing {d.markets.length} of {d.count}. Search to narrow down.</p>}</>}
      </section>
      <div className="grid lg:grid-cols-2 gap-4 items-start"><PicksBotCard onOpen={setOpen} /><Leaderboard /></div>
      <MarketSheet slug={open} onClose={() => setOpen(null)} />
    </div>
  );
}
